import React, { useState, useEffect } from "react";
import { Modal, Stack, Select, Button, Alert, Group } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { Employee } from "../../types/salon";
import {
  CommissionRecord,
  CreateCommissionInput,
} from "../../types/commission";
import NewCommissionForm from "./NewCommissionForm";

interface CommissionModalProps {
  opened: boolean;
  onClose: () => void;
  employees: Employee[];
  editingCommission?: CommissionRecord | null;
  onSaved: () => void;
}

const StaffCommissionModal: React.FC<CommissionModalProps> = ({
  opened,
  onClose,
  employees,
  editingCommission = null,
  onSaved,
}) => {
  const { createCommissions } = useApp();
  const [step, setStep] = useState<"pick-staff" | "form">("pick-staff");
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!editingCommission;

  // When modal opens, set initial step based on mode
  useEffect(() => {
    if (opened) {
      if (isEditMode) {
        setStep("form");
        setSelectedStaffId(editingCommission!.staff_id);
      } else {
        setStep("pick-staff");
        setSelectedStaffId(null);
      }
      setError(null);
    }
  }, [opened, isEditMode, editingCommission]);

  const handleClose = () => {
    setStep("pick-staff");
    setSelectedStaffId(null);
    setError(null);
    onClose();
  };

  const handleStaffNext = () => {
    if (selectedStaffId) {
      setStep("form");
    }
  };

  const handleSubmit = async (input: CreateCommissionInput) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await createCommissions({ commissions: [input] });
      onSaved();
      handleClose();
    } catch (err: any) {
      console.error("Error saving commission:", err);
      setError(err?.message || "Failed to save commission. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const staffData = employees.map((emp) => ({
    value: emp.id,
    label: `${emp.first_name} ${emp.last_name}`,
  }));

  const staffId = selectedStaffId || editingCommission?.staff_id || "";

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? "Edit Commission" : "Add New Commission"}
      size="lg"
      centered
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {step === "pick-staff" && (
          <>
            <Select
              label="Select a staff member"
              placeholder="Search staff..."
              data={staffData}
              value={selectedStaffId}
              onChange={setSelectedStaffId}
              searchable
            />
            <Group justify="flex-end">
              <Button
                variant="light"
                color="gray"
                onClick={handleClose}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                color="var(--brand-teal)"
                onClick={handleStaffNext}
                disabled={!selectedStaffId}
                size="sm"
              >
                Next
              </Button>
            </Group>
          </>
        )}

        {step === "form" && staffId && (
          <NewCommissionForm
            staffId={staffId}
            existingCommission={editingCommission}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            isLoading={isSubmitting}
          />
        )}
      </Stack>
    </Modal>
  );
};

export default StaffCommissionModal;
